import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import citiesData from '../../data/cities_with_coordinates.json';
import UserAvatar from './UserAvatar';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';

// ─── Types ────────────────────────────────────────────────────────────────────

type DemandStatus = 'NAO_INICIADA' | 'EM_ANDAMENTO' | 'PAUSADA' | 'CANCELADA' | 'CONCLUIDA';
type DemandType = 'INDIVIDUAL' | 'CIDADE' | 'ENTIDADE' | 'GRUPO';
type DemandSize = 'PEQUENA' | 'MEDIA' | 'GRANDE';
type DemandNature = 'EXCLUSIVA' | 'INTERMEDIADA';
type SheetKind =
  | 'responsavel' | 'cidade' | 'solicitante' | 'tipo'
  | 'natureza' | 'tamanho' | 'previsao' | 'intermediador' | 'titulo';

interface Demand {
  id: number;
  titulo: string;
  descricao?: string;
  tipo: DemandType;
  tamanho?: DemandSize;
  natureza?: DemandNature;
  status: DemandStatus;
  data_cadastro: string;
  previsao_conclusao?: string;
  cidade_id?: number | null;
  solicitante_id?: number | null;
  cidade?: { id?: number; nome: string };
  solicitante?: { id?: number; nome: string };
  responsavel?: { id: number; nome: string; foto_url?: string | null };
  intermediador?: string;
}

interface Usuario {
  id: number;
  nome: string;
  tipo_usuario: 'ADMIN' | 'ASSESSOR';
  nivel_acesso: string;
  foto_url?: string | null;
}

interface Registro {
  id: number;
  descricao_atualizacao: string;
  data: string;
  status_na_data?: DemandStatus;
  created_at: string;
  updated_at: string;
  usuario: { id: number; nome: string };
}

interface HistoricoItem {
  id: number;
  observacao: string;
  data_hora: string;
  usuario: { id: number; nome: string } | null;
}

export interface DemandDetailModalMobileProps {
  isOpen: boolean;
  demand: Demand | null;
  onClose: () => void;
  onUpdate: () => void;
  registros?: Registro[];
  loadingRegistros?: boolean;
  newRegistro?: string;
  onNewRegistroChange?: (value: string) => void;
  onAddRegistro?: () => void;
  addingRegistro?: boolean;
  editingRegistro?: number | null;
  editingText?: string;
  onEditRegistro?: (id: number) => void;
  onSaveEdit?: (id: number) => void;
  onCancelEdit?: () => void;
  onEditTextChange?: (value: string) => void;
  onDeleteRegistro?: (id: number) => void;
  savingRegistroId?: number | null;
  deletingRegistroId?: number | null;
  openMenuId?: number | null;
  onMenuToggle?: (id: number | null) => void;
  canViewUpdates?: boolean;
  canCreateUpdates?: boolean;
  canEditUpdate?: (registro: Registro) => boolean;
  canDeleteUpdate?: (registro: Registro) => boolean;
  canEditDemand?: boolean;
  canDeleteDemand?: boolean;
  canAssignResponsible?: boolean;
  canUnassignResponsible?: boolean;
  userData?: Usuario | null;
  historico?: HistoricoItem[];
  loadingHistorico?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<DemandStatus, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CANCELADA: 'Cancelada',
  CONCLUIDA: 'Concluída',
};

const STATUS_COLOR: Record<DemandStatus, string> = {
  NAO_INICIADA: '#64748b',
  EM_ANDAMENTO: '#3b82f6',
  PAUSADA: '#f59e0b',
  CANCELADA: '#ef4444',
  CONCLUIDA: '#10b981',
};

const STATUS_BG: Record<DemandStatus, string> = {
  NAO_INICIADA: '#f1f5f9',
  EM_ANDAMENTO: '#eff6ff',
  PAUSADA: '#fffbeb',
  CANCELADA: '#fef2f2',
  CONCLUIDA: '#f0fdf4',
};

const SIZE_FILLED: Record<DemandSize, number> = { PEQUENA: 1, MEDIA: 3, GRANDE: 5 };
const SIZE_LETTER: Record<DemandSize, string> = { PEQUENA: 'P', MEDIA: 'M', GRANDE: 'G' };

const NATURE_LABEL: Record<DemandNature, string> = {
  EXCLUSIVA: 'Exclusiva',
  INTERMEDIADA: 'Intermediada',
};

const TYPE_LABEL: Record<DemandType, string> = {
  INDIVIDUAL: 'Individual',
  CIDADE: 'Cidade',
  ENTIDADE: 'Entidade',
  GRUPO: 'Grupo',
};

// Step index: CANCELADA = -1 (special), PAUSADA maps to step 1
const STEP_INDEX: Record<DemandStatus, number> = {
  NAO_INICIADA: 0,
  EM_ANDAMENTO: 1,
  PAUSADA: 1,
  CONCLUIDA: 2,
  CANCELADA: -1,
};
const STEP_LABELS = ['Não iniciada', 'Em andamento', 'Concluída'];

interface ActionBtn { label: string; status: DemandStatus; variant: 'primary' | 'secondary' | 'danger' | 'warning'; icon: string; }
const STATUS_ACTIONS: Record<DemandStatus, ActionBtn[]> = {
  NAO_INICIADA: [
    { label: 'Iniciar', status: 'EM_ANDAMENTO', variant: 'primary', icon: '▶' },
    { label: 'Cancelar', status: 'CANCELADA', variant: 'danger', icon: '✕' },
  ],
  EM_ANDAMENTO: [
    { label: 'Concluir', status: 'CONCLUIDA', variant: 'primary', icon: '✓' },
    { label: 'Pausar', status: 'PAUSADA', variant: 'warning', icon: '⏸' },
    { label: 'Cancelar', status: 'CANCELADA', variant: 'danger', icon: '✕' },
  ],
  PAUSADA: [
    { label: 'Retomar', status: 'EM_ANDAMENTO', variant: 'primary', icon: '▶' },
    { label: 'Concluir', status: 'CONCLUIDA', variant: 'secondary', icon: '✓' },
    { label: 'Cancelar', status: 'CANCELADA', variant: 'danger', icon: '✕' },
  ],
  CONCLUIDA: [
    { label: 'Reabrir', status: 'EM_ANDAMENTO', variant: 'secondary', icon: '↩' },
    { label: 'Cancelar', status: 'CANCELADA', variant: 'danger', icon: '✕' },
  ],
  CANCELADA: [
    { label: 'Reabrir', status: 'NAO_INICIADA', variant: 'secondary', icon: '↩' },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ago = (d: string) => {
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'ontem';
  if (days < 7) return `${days}d`;
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

const calcSLA = (dataCadastro: string, status: DemandStatus) => {
  if (status === 'CONCLUIDA' || status === 'CANCELADA') return 0;
  const diff = Math.abs(Date.now() - new Date(dataCadastro).getTime());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const slaColor = (days: number) => days === 0 ? '#10b981' : days <= 7 ? '#10b981' : days <= 30 ? '#f59e0b' : '#ef4444';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(100%); } to { transform: translateY(0); }`;

// ─── Overlay & Sheet ──────────────────────────────────────────────────────────

const Overlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 3100;
    flex-direction: column;
    opacity: ${p => (p.$open ? 1 : 0)};
    pointer-events: ${p => (p.$open ? 'auto' : 'none')};
    transition: opacity 0.24s ease;
  }
`;

const Sheet = styled.div<{ $open: boolean }>`
  background: #f8fafc;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${p => (p.$open ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

// ─── Nav ──────────────────────────────────────────────────────────────────────

const Nav = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 12px 8px 10px;
  background: rgba(248, 250, 252, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const NavIconBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(120, 120, 128, 0.12);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007aff;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
  &:active { background: rgba(120, 120, 128, 0.24); }
`;

const NavCenter = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 6px;
  overflow: hidden;
`;

const NavSup = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1;
`;

const NavTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Scrollable body ──────────────────────────────────────────────────────────

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 90px;
`;

// ─── Crumbs ───────────────────────────────────────────────────────────────────

const Crumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px 0;
  flex-wrap: wrap;
`;

const Pill = styled.span<{ $color?: string; $bg?: string }>`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${p => p.$bg || '#f1f5f9'};
  color: ${p => p.$color || '#64748b'};
  letter-spacing: 0.02em;
`;

const PillDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$color};
  display: inline-block;
  margin-right: 4px;
  vertical-align: middle;
`;

const CrumbSep = styled.span`
  color: #cbd5e1;
  font-size: 12px;
`;

// ─── Hero title ───────────────────────────────────────────────────────────────

const HeroWrap = styled.div`
  padding: 12px 16px 0;
`;

const HeroTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
`;

// ─── Cards ────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  margin: 12px 16px 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

const CardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 12px 14px 6px;
`;

// ─── Description card ─────────────────────────────────────────────────────────

const DescCard = styled(Card)`
  border-left: 3px solid #10b981;
`;

const DescBody = styled.div`
  padding: 0 14px 12px;
  min-height: 40px;
`;

const DescText = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DescEmpty = styled.div`
  font-size: 14px;
  color: #94a3b8;
  font-style: italic;
`;

const DescEditArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  font-family: inherit;
  box-sizing: border-box;
`;

const DescFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px 10px;
  border-top: 1px solid #f1f5f9;
`;

const DescSolicitante = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const DescEditBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--accent);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DescSaveRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 14px 10px;
  justify-content: flex-end;
`;

const SmallBtn = styled.button<{ $primary?: boolean }>`
  padding: 5px 14px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${p => p.$primary ? '#3b82f6' : '#e2e8f0'};
  background: ${p => p.$primary ? '#3b82f6' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#374151'};
`;

// ─── Status card ──────────────────────────────────────────────────────────────

const SLABanner = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${p => p.$color}18;
  border-bottom: 1px solid ${p => p.$color}30;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
`;

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 14px 10px;
  gap: 0;
`;

const StepCircle = styled.div<{ $state: 'done' | 'active' | 'idle' | 'cancelled' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  ${p => {
    if (p.$state === 'done') return 'background:#3b82f6;color:#fff;';
    if (p.$state === 'active') return 'background:#3b82f6;color:#fff;box-shadow:0 0 0 3px #bfdbfe;';
    if (p.$state === 'cancelled') return 'background:#fef2f2;color:#ef4444;border:1.5px solid #fca5a5;';
    return 'background:#f1f5f9;color:#94a3b8;';
  }}
`;

const StepConnector = styled.div<{ $filled: boolean }>`
  flex: 1;
  height: 2px;
  background: ${p => p.$filled ? '#3b82f6' : '#e2e8f0'};
`;

const StepLabels = styled.div`
  display: flex;
  padding: 0 14px 12px;
`;

const StepLabelItem = styled.div<{ $active: boolean }>`
  flex: 1;
  text-align: center;
  font-size: 10px;
  font-weight: ${p => p.$active ? 700 : 500};
  color: ${p => p.$active ? '#3b82f6' : '#94a3b8'};
  &:first-child { text-align: left; }
  &:last-child { text-align: right; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 14px 14px;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button<{ $variant: 'primary' | 'secondary' | 'danger' | 'warning' }>`
  flex: 1;
  min-width: 80px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  ${p => {
    if (p.$variant === 'primary') return 'background:#3b82f6;color:#fff;border-color:#3b82f6;';
    if (p.$variant === 'danger') return 'background:#fff;color:#ef4444;border-color:#fca5a5;';
    if (p.$variant === 'warning') return 'background:#fffbeb;color:#d97706;border-color:#fcd34d;';
    return 'background:#f8fafc;color:#374151;border-color:#e2e8f0;';
  }}
  &:disabled { opacity: 0.5; }
`;

// ─── Details rows ─────────────────────────────────────────────────────────────

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  padding: 11px 14px;
  border-top: 1px solid #f1f5f9;
  cursor: pointer;
  &:active { background: #f8fafc; }
  &:first-child { border-top: none; }
`;

const DetailIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { width: 16px; height: 16px; color: #64748b; }
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: #64748b;
  flex: 1;
  margin-left: 10px;
`;

const DetailValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
  max-width: 55%;
  text-align: right;
  word-break: break-word;
`;

const ChevronRight = () => (
  <svg fill="none" stroke="#cbd5e1" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 14, height: 14, flexShrink: 0 }}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);

// ─── SizeDots ─────────────────────────────────────────────────────────────────

const SizeDotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const SizeDot = styled.div<{ $filled: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$filled ? '#3b82f6' : '#e2e8f0'};
`;

const SizeLetterBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #3b82f6;
  margin-left: 3px;
`;

const SizeDots: React.FC<{ size: DemandSize }> = ({ size }) => {
  const filled = SIZE_FILLED[size];
  return (
    <SizeDotsRow>
      {[1,2,3,4,5].map(i => <SizeDot key={i} $filled={i <= filled} />)}
      <SizeLetterBadge>{SIZE_LETTER[size]}</SizeLetterBadge>
    </SizeDotsRow>
  );
};

// ─── Feed ─────────────────────────────────────────────────────────────────────

const FeedHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 14px 6px;
  gap: 6px;
`;

const FeedLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  flex: 1;
`;

const FeedCount = styled.span`
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
`;

const FeedItem = styled.div`
  padding: 12px 14px;
  border-top: 1px solid #f1f5f9;
`;

const FeedItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
`;

const FeedAvatar = styled.div<{ $system?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => p.$system ? '#f0fdf4' : 'rgba(var(--accent-rgb), 0.08)'};
  color: ${p => p.$system ? '#16a34a' : 'var(--accent)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const FeedUser = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  flex: 1;
`;

const FeedTime = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

const FeedText = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  padding-left: 36px;
`;

const FeedDateGroup = styled.div`
  padding: 8px 14px 2px;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const EmptyFeed = styled.div`
  padding: 24px 14px;
  text-align: center;
  font-size: 13px;
  color: #94a3b8;
`;

// ─── Composer bar ─────────────────────────────────────────────────────────────

const ComposerBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3200;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  padding: 10px 12px;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  display: flex;
  align-items: flex-end;
  gap: 8px;
  display: none;
  @media (max-width: 900px) { display: flex; }
`;

const ComposerInput = styled.textarea`
  flex: 1;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 20px;
  padding: 9px 14px;
  font-size: 14px;
  color: #0f172a;
  font-family: inherit;
  resize: none;
  outline: none;
  max-height: 100px;
  line-height: 1.45;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const ComposerSend = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:disabled { background: #e2e8f0; }
  svg { width: 16px; height: 16px; color: #fff; }
`;

// ─── Bottom Sheet (generic) ───────────────────────────────────────────────────

const SheetOverlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 4000;
    background: rgba(0,0,0,${p => p.$open ? 0.4 : 0});
    pointer-events: ${p => p.$open ? 'auto' : 'none'};
    transition: background 0.24s;
  }
`;

const BottomSheet = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 4100;
    background: #fff;
    border-radius: 20px 20px 0 0;
    max-height: 80vh;
    transform: ${p => p.$open ? 'translateY(0)' : 'translateY(100%)'};
    transition: transform 0.3s cubic-bezier(0.2,0.8,0.2,1);
  }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 10px auto 0;
`;

const SheetTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  padding: 14px 18px 10px;
  border-bottom: 1px solid #f1f5f9;
`;

const SheetList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const SheetOption = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 13px 18px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  border: none;
  text-align: left;
  font-size: 15px;
  color: ${p => p.$active ? 'var(--accent)' : '#0f172a'};
  font-weight: ${p => p.$active ? 700 : 400};
  cursor: pointer;
  border-bottom: 1px solid #f8fafc;
  display: flex;
  align-items: center;
  gap: 10px;
  &:active { background: #f1f5f9; }
`;

const SheetInput = styled.input`
  margin: 10px 16px;
  padding: 10px 12px;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  width: calc(100% - 32px);
  box-sizing: border-box;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const SheetDateInput = styled.input`
  margin: 10px 16px;
  padding: 10px 12px;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  width: calc(100% - 32px);
  box-sizing: border-box;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const SheetActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
`;

const SheetBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${p => p.$primary ? '#3b82f6' : '#e2e8f0'};
  background: ${p => p.$primary ? '#3b82f6' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#374151'};
  &:disabled { opacity: 0.5; }
`;

// ─── Misc ──────────────────────────────────────────────────────────────────────

const shimmer = keyframes`
  from { background-position: -200% 0; }
  to   { background-position:  200% 0; }
`;

const shimmerBg = css`
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
  border-radius: 6px;
`;

const ShimmerLine = styled.div<{ $w: string | number; $h: number }>`
  ${shimmerBg}
  width: ${p => typeof p.$w === 'number' ? `${p.$w}px` : p.$w};
  height: ${p => p.$h}px;
`;

const ShimmerCircle = styled.div`
  ${shimmerBg}
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const Spinner = styled.div`
  width: 16px; height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${keyframes`to{transform:rotate(360deg)}`} 0.7s linear infinite;
  display: inline-block;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DemandDetailModalMobile: React.FC<DemandDetailModalMobileProps> = ({
  isOpen,
  demand,
  onClose,
  onUpdate,
  registros = [],
  loadingRegistros = false,
  newRegistro = '',
  onNewRegistroChange,
  onAddRegistro,
  addingRegistro = false,
  editingRegistro = null,
  editingText = '',
  onEditRegistro,
  onSaveEdit,
  onCancelEdit,
  onEditTextChange,
  onDeleteRegistro,
  savingRegistroId = null,
  deletingRegistroId = null,
  openMenuId = null,
  onMenuToggle,
  canViewUpdates = false,
  canCreateUpdates = false,
  canEditUpdate,
  canDeleteUpdate,
  canEditDemand = false,
  canDeleteDemand = false,
  canAssignResponsible = false,
  canUnassignResponsible = false,
  userData: userDataProp,
  historico = [],
  loadingHistorico = false,
}) => {
  const { session, userData: authUserData } = useAuth();
  const userData = userDataProp || authUserData;

  const [currentDemand, setCurrentDemand] = useState<Demand | null>(demand ?? null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Editing description inline
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  // Bottom sheet state
  const [sheetKind, setSheetKind] = useState<SheetKind | null>(null);
  const [sheetSearch, setSheetSearch] = useState('');
  const [sheetDate, setSheetDate] = useState('');

  // Users & eleitores
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [eleitores, setEleitores] = useState<Array<{ id: number; nome: string }>>([]);

  useEffect(() => {
    if (isOpen && demand) {
      setCurrentDemand(demand);
    }
  }, [isOpen, demand]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const fetchUsuarios = async () => {
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` }
      });
      if (r.ok) {
        const d = await r.json();
        if (d.success) setUsuarios(d.data);
      }
    } catch {}
  };

  const fetchEleitores = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/eleitores`, {
        headers: { 'Authorization': `Bearer ${session?.access_token}` }
      });
      const d = await r.json();
      setEleitores(Array.isArray(d) ? d : (d.data || []));
    } catch { setEleitores([]); }
  };

  const saveField = async (fieldKey: string, body: Record<string, any>, localPatch?: Partial<Demand>) => {
    if (!currentDemand || !session?.access_token) return;
    setSavingField(fieldKey);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      setCurrentDemand({ ...currentDemand, ...(localPatch ?? body) } as Demand);
      onUpdate();
    } catch { alert('Erro ao salvar'); }
    finally { setSavingField(null); }
  };

  const handleUpdateStatus = async (newStatus: DemandStatus) => {
    if (!currentDemand) return;
    setUpdatingStatus(true);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (r.ok) { setCurrentDemand({ ...currentDemand, status: newStatus }); onUpdate(); }
    } catch {} finally { setUpdatingStatus(false); }
  };

  const openSheet = (kind: SheetKind) => {
    setSheetSearch('');
    if (kind === 'previsao' && currentDemand?.previsao_conclusao) {
      setSheetDate(currentDemand.previsao_conclusao.slice(0, 10));
    } else {
      setSheetDate('');
    }
    // Lazy-load dropdown data only when actually needed
    if (kind === 'responsavel' && usuarios.length === 0) fetchUsuarios();
    if ((kind === 'solicitante') && eleitores.length === 0) fetchEleitores();
    setSheetKind(kind);
  };
  const closeSheet = () => setSheetKind(null);

  const handleSheetSave = async () => {
    if (!currentDemand || !sheetKind) return;
    if (sheetKind === 'previsao') {
      await saveField('previsao_conclusao', { previsao_conclusao: sheetDate || null });
      closeSheet();
      return;
    }
    closeSheet();
  };

  // Never return null — keep mounted so styled-components stay registered (no cold-mount delay).
  // The Overlay already hides itself via opacity/pointer-events when $open=false.
  if (!currentDemand) return <Overlay $open={false} />;

  const sla = calcSLA(currentDemand.data_cadastro, currentDemand.status);
  const sc = slaColor(sla);
  const stepIdx = STEP_INDEX[currentDemand.status];
  const cancelled = currentDemand.status === 'CANCELADA';

  // Format demand code
  const year = new Date(currentDemand.data_cadastro).getFullYear();
  const demandCode = `#DMD-${year}-${String(currentDemand.id).padStart(4, '0')}`;

  // Build merged feed
  type TEntry = { kind: 'registro'; data: Registro; ts: Date } | { kind: 'historico'; data: HistoricoItem; ts: Date };
  const allEntries: TEntry[] = [];
  registros.forEach(r => allEntries.push({ kind: 'registro', data: r, ts: new Date(r.created_at) }));
  historico.forEach(h => allEntries.push({ kind: 'historico', data: h, ts: new Date(h.data_hora) }));
  allEntries.sort((a, b) => a.ts.getTime() - b.ts.getTime());

  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const toDS = (d: Date) => d.toISOString().slice(0, 10);

  // cities RS
  const rsCities = (citiesData as any[]).filter(c => c.microrregiao?.mesorregiao?.UF?.sigla === 'RS');

  // Sheet option lists
  const sheetOptions: Record<string, { value: string; label: string }[]> = {
    tipo: [
      { value: 'INDIVIDUAL', label: 'Individual' },
      { value: 'CIDADE', label: 'Cidade' },
      { value: 'ENTIDADE', label: 'Entidade' },
      { value: 'GRUPO', label: 'Grupo' },
    ],
    natureza: [
      { value: 'EXCLUSIVA', label: 'Exclusiva' },
      { value: 'INTERMEDIADA', label: 'Intermediada' },
    ],
    tamanho: [
      { value: 'PEQUENA', label: 'Pequena' },
      { value: 'MEDIA', label: 'Média' },
      { value: 'GRANDE', label: 'Grande' },
    ],
    cidade: rsCities.map(c => ({ value: String(c.id), label: c.nome })),
    solicitante: eleitores.map(e => ({ value: String(e.id), label: e.nome })),
    responsavel: usuarios.map(u => ({ value: String(u.id), label: u.nome })),
  };

  const currentSheetOptions = sheetKind && sheetOptions[sheetKind]
    ? sheetOptions[sheetKind].filter(o => o.label.toLowerCase().includes(sheetSearch.toLowerCase()))
    : [];

  const handleOptionSelect = async (value: string) => {
    if (!sheetKind) return;
    if (sheetKind === 'tipo') { await saveField('tipo', { tipo: value }); }
    else if (sheetKind === 'natureza') {
      const patch = value === 'EXCLUSIVA' ? { natureza: value, intermediador: null } : { natureza: value };
      await saveField('natureza', patch, value === 'EXCLUSIVA' ? { natureza: value as DemandNature, intermediador: undefined } : { natureza: value as DemandNature });
    }
    else if (sheetKind === 'tamanho') { await saveField('tamanho', { tamanho: value }); }
    else if (sheetKind === 'cidade') {
      const id = parseInt(value, 10);
      const city = rsCities.find(c => c.id === id);
      await saveField('cidade_id', { cidade_id: id }, { cidade_id: id, cidade: { id, nome: city?.nome ?? '' } });
    }
    else if (sheetKind === 'solicitante') {
      const id = parseInt(value, 10);
      const eleitor = eleitores.find(e => e.id === id);
      await saveField('solicitante_id', { solicitante_id: id }, { solicitante_id: id, solicitante: { id, nome: eleitor?.nome ?? '' } });
    }
    else if (sheetKind === 'responsavel') {
      const id = parseInt(value, 10);
      const u = usuarios.find(u => u.id === id);
      if (u) {
        setSavingField('responsavel');
        try {
          const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
            body: JSON.stringify({ responsavel_id: id }),
          });
          if (r.ok) { setCurrentDemand({ ...currentDemand, responsavel: { id: u.id, nome: u.nome } }); onUpdate(); }
        } catch {} finally { setSavingField(null); }
      }
    }
    closeSheet();
  };

  const sheetNeedsSearch = sheetKind && ['cidade','solicitante','responsavel'].includes(sheetKind);

  return (
    <>
      <Overlay $open={isOpen}>
        <Sheet $open={isOpen}>
          {/* Nav */}
          <Nav>
            <NavIconBtn onClick={onClose} aria-label="Voltar">
              <svg width="10" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 10 16">
                <path d="M8 1L1 8 8 15" />
              </svg>
            </NavIconBtn>
            <NavCenter>
              <NavSup>Demanda</NavSup>
              <NavTitle>{demandCode}</NavTitle>
            </NavCenter>
            <NavIconBtn aria-label="Mais opções">
              <svg width="4" height="16" fill="currentColor" viewBox="0 0 4 16">
                <circle cx="2" cy="2"  r="1.5" />
                <circle cx="2" cy="8"  r="1.5" />
                <circle cx="2" cy="14" r="1.5" />
              </svg>
            </NavIconBtn>
          </Nav>

          <Body>
            {/* Breadcrumbs */}
            <Crumbs>
              {currentDemand.cidade && (
                <>
                  <Pill>{currentDemand.cidade.nome}</Pill>
                  <CrumbSep>›</CrumbSep>
                </>
              )}
              <Pill>Demanda</Pill>
              <CrumbSep>›</CrumbSep>
              <Pill $color={STATUS_COLOR[currentDemand.status]} $bg={STATUS_BG[currentDemand.status]}>
                <PillDot $color={STATUS_COLOR[currentDemand.status]} />
                {STATUS_LABEL[currentDemand.status]}
              </Pill>
            </Crumbs>

            {/* Hero Title */}
            <HeroWrap>
              <HeroTitle
                onClick={() => canEditDemand && openSheet('titulo')}
                style={{ cursor: canEditDemand ? 'pointer' : 'default' }}
              >
                {currentDemand.titulo || 'Sem título'}
              </HeroTitle>
            </HeroWrap>

            {/* Description card */}
            <DescCard>
              <CardLabel>Descrição</CardLabel>
              <DescBody>
                {editingDesc ? (
                  <DescEditArea
                    value={descDraft}
                    autoFocus
                    onChange={e => setDescDraft(e.target.value)}
                  />
                ) : currentDemand.descricao ? (
                  <DescText>{currentDemand.descricao}</DescText>
                ) : (
                  <DescEmpty>Sem descrição</DescEmpty>
                )}
              </DescBody>
              {editingDesc ? (
                <DescSaveRow>
                  <SmallBtn onClick={() => setEditingDesc(false)}>Cancelar</SmallBtn>
                  <SmallBtn $primary disabled={savingField === 'descricao'} onClick={async () => {
                    await saveField('descricao', { descricao: descDraft });
                    setEditingDesc(false);
                  }}>
                    {savingField === 'descricao' ? <Spinner /> : 'Salvar'}
                  </SmallBtn>
                </DescSaveRow>
              ) : (
                <DescFooter>
                  <DescSolicitante>
                    {currentDemand.solicitante ? `Solicitante: ${currentDemand.solicitante.nome}` : ''}
                  </DescSolicitante>
                  {canEditDemand && (
                    <DescEditBtn onClick={() => { setDescDraft(currentDemand.descricao || ''); setEditingDesc(true); }}>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 14, height: 14 }}>
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Editar
                    </DescEditBtn>
                  )}
                </DescFooter>
              )}
            </DescCard>

            {/* Status card */}
            <Card>
              {/* SLA banner */}
              {sla > 0 && (
                <SLABanner $color={sc}>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 14, height: 14 }}>
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  SLA: {sla} dia{sla !== 1 ? 's' : ''}
                </SLABanner>
              )}
              <CardLabel>Status</CardLabel>

              {/* Stepper */}
              <StepperRow>
                {STEP_LABELS.map((label, i) => {
                  const state = cancelled ? 'cancelled' : stepIdx > i ? 'done' : stepIdx === i ? 'active' : 'idle';
                  return (
                    <React.Fragment key={label}>
                      <StepCircle $state={state}>
                        {cancelled ? '✕' : state === 'done' ? '✓' : i + 1}
                      </StepCircle>
                      {i < STEP_LABELS.length - 1 && (
                        <StepConnector $filled={!cancelled && stepIdx > i} />
                      )}
                    </React.Fragment>
                  );
                })}
              </StepperRow>
              <StepLabels>
                {STEP_LABELS.map((label, i) => (
                  <StepLabelItem key={label} $active={!cancelled && stepIdx === i}>
                    {label}
                  </StepLabelItem>
                ))}
              </StepLabels>

              {/* Action buttons */}
              {canEditDemand && (
                <ActionRow>
                  {STATUS_ACTIONS[currentDemand.status].map(btn => (
                    <ActionBtn
                      key={btn.label}
                      $variant={btn.variant}
                      disabled={updatingStatus}
                      onClick={() => handleUpdateStatus(btn.status)}
                    >
                      {updatingStatus ? <Spinner /> : <>{btn.icon} {btn.label}</>}
                    </ActionBtn>
                  ))}
                </ActionRow>
              )}
            </Card>

            {/* Details card */}
            <Card>
              <CardLabel>Detalhes</CardLabel>

              {/* Responsável */}
              <DetailRow onClick={() => (canAssignResponsible || canUnassignResponsible) && openSheet('responsavel')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Responsável</DetailLabel>
                <DetailValue>
                  {currentDemand.responsavel ? (
                    <>
                      <UserAvatar name={currentDemand.responsavel.nome} fotoUrl={currentDemand.responsavel.foto_url} size={20} />
                      {currentDemand.responsavel.nome}
                    </>
                  ) : <span style={{ color: '#94a3b8' }}>—</span>}
                </DetailValue>
                {(canAssignResponsible || canUnassignResponsible) && <ChevronRight />}
              </DetailRow>

              {/* Cidade */}
              <DetailRow onClick={() => canEditDemand && openSheet('cidade')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Cidade</DetailLabel>
                <DetailValue>{currentDemand.cidade?.nome || <span style={{ color: '#94a3b8' }}>—</span>}</DetailValue>
                {canEditDemand && <ChevronRight />}
              </DetailRow>

              {/* Solicitante */}
              <DetailRow onClick={() => canEditDemand && openSheet('solicitante')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 11-8 0 4 4 0 018 0zM21 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Solicitante</DetailLabel>
                <DetailValue>{currentDemand.solicitante?.nome || <span style={{ color: '#94a3b8' }}>—</span>}</DetailValue>
                {canEditDemand && <ChevronRight />}
              </DetailRow>

              {/* Tipo */}
              <DetailRow onClick={() => canEditDemand && openSheet('tipo')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Tipo</DetailLabel>
                <DetailValue>{TYPE_LABEL[currentDemand.tipo] || <span style={{ color: '#94a3b8' }}>—</span>}</DetailValue>
                {canEditDemand && <ChevronRight />}
              </DetailRow>

              {/* Natureza */}
              <DetailRow onClick={() => canEditDemand && openSheet('natureza')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Natureza</DetailLabel>
                <DetailValue>{currentDemand.natureza ? NATURE_LABEL[currentDemand.natureza] : <span style={{ color: '#94a3b8' }}>—</span>}</DetailValue>
                {canEditDemand && <ChevronRight />}
              </DetailRow>

              {/* Tamanho */}
              <DetailRow onClick={() => canEditDemand && openSheet('tamanho')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Tamanho</DetailLabel>
                <DetailValue>
                  {currentDemand.tamanho ? <SizeDots size={currentDemand.tamanho} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                </DetailValue>
                {canEditDemand && <ChevronRight />}
              </DetailRow>

              {/* Previsão */}
              <DetailRow onClick={() => canEditDemand && openSheet('previsao')}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Previsão</DetailLabel>
                <DetailValue>{currentDemand.previsao_conclusao ? fmtDate(currentDemand.previsao_conclusao) : <span style={{ color: '#94a3b8' }}>—</span>}</DetailValue>
                {canEditDemand && <ChevronRight />}
              </DetailRow>

              {/* SLA */}
              <DetailRow style={{ cursor: 'default' }}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </DetailIcon>
                <DetailLabel>SLA</DetailLabel>
                <DetailValue style={{ color: sc }}>{sla === 0 ? 'Concluída' : `${sla} dias`}</DetailValue>
              </DetailRow>

              {/* Cadastrado */}
              <DetailRow style={{ cursor: 'default' }}>
                <DetailIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </DetailIcon>
                <DetailLabel>Cadastrado</DetailLabel>
                <DetailValue>{fmtDate(currentDemand.data_cadastro)}</DetailValue>
              </DetailRow>

              {/* Intermediador */}
              {(currentDemand.natureza === 'INTERMEDIADA' || currentDemand.intermediador) && (
                <DetailRow onClick={() => canEditDemand && openSheet('intermediador')}>
                  <DetailIcon>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </DetailIcon>
                  <DetailLabel>Intermediador</DetailLabel>
                  <DetailValue>{currentDemand.intermediador || <span style={{ color: '#94a3b8' }}>—</span>}</DetailValue>
                  {canEditDemand && <ChevronRight />}
                </DetailRow>
              )}
            </Card>

            {/* Feed card */}
            {canViewUpdates && (
              <Card style={{ marginBottom: 16 }}>
                <FeedHeader>
                  <FeedLabel>Registros</FeedLabel>
                  <FeedCount>{allEntries.length}</FeedCount>
                </FeedHeader>

                {(loadingRegistros || loadingHistorico) ? (
                  <>
                    {[1,2,3].map(i => (
                      <FeedItem key={i}>
                        <FeedItemHeader>
                          <ShimmerCircle />
                          <ShimmerLine $w="45%" $h={12} />
                          <ShimmerLine $w="15%" $h={10} style={{ marginLeft: 'auto' }} />
                        </FeedItemHeader>
                        <div style={{ paddingLeft: 36 }}>
                          <ShimmerLine $w="90%" $h={12} style={{ marginBottom: 6 }} />
                          <ShimmerLine $w={i === 2 ? '60%' : '75%'} $h={12} />
                        </div>
                      </FeedItem>
                    ))}
                  </>
                ) : allEntries.length === 0 ? (
                  <EmptyFeed>Nenhum registro ainda</EmptyFeed>
                ) : (() => {
                  let lastGroup = '';
                  return allEntries.map((entry, idx) => {
                    const ds = toDS(entry.ts);
                    let label = '';
                    if (ds === toDS(today)) label = 'Hoje';
                    else if (ds === toDS(yesterday)) label = `Ontem · ${entry.ts.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
                    else label = entry.ts.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

                    const groupEl = label !== lastGroup ? (lastGroup = label, <FeedDateGroup key={`grp-${ds}-${idx}`}>{label}</FeedDateGroup>) : null;

                    if (entry.kind === 'historico') {
                      const h = entry.data;
                      const authorName = h.usuario?.nome || 'Sistema';
                      const isSelf = userData && h.usuario && h.usuario.id === userData.id;
                      return (
                        <React.Fragment key={`h-${h.id}`}>
                          {groupEl}
                          <FeedItem>
                            <FeedItemHeader>
                              <FeedAvatar $system>
                                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 14, height: 14 }}>
                                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              </FeedAvatar>
                              <FeedUser>{isSelf ? 'Você' : authorName}</FeedUser>
                              <FeedTime>{ago(h.data_hora)}</FeedTime>
                            </FeedItemHeader>
                            <FeedText>{h.observacao}</FeedText>
                          </FeedItem>
                        </React.Fragment>
                      );
                    } else {
                      const r = entry.data;
                      const isSelf = userData && r.usuario.id === userData.id;
                      return (
                        <React.Fragment key={`r-${r.id}`}>
                          {groupEl}
                          <FeedItem>
                            <FeedItemHeader>
                              <FeedAvatar>{r.usuario.nome.charAt(0).toUpperCase()}</FeedAvatar>
                              <FeedUser>{isSelf ? 'Você' : r.usuario.nome}</FeedUser>
                              <FeedTime>{ago(r.created_at)}</FeedTime>
                            </FeedItemHeader>
                            <FeedText>{r.descricao_atualizacao}</FeedText>
                          </FeedItem>
                        </React.Fragment>
                      );
                    }
                  });
                })()}
              </Card>
            )}
          </Body>

          {/* Composer */}
          {canCreateUpdates && (
            <ComposerBar>
              <ComposerInput
                placeholder="Adicionar registro..."
                value={newRegistro}
                onChange={e => onNewRegistroChange && onNewRegistroChange(e.target.value)}
                rows={1}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onAddRegistro && onAddRegistro(); }}
              />
              <ComposerSend
                disabled={!newRegistro.trim() || addingRegistro}
                onClick={() => onAddRegistro && onAddRegistro()}
              >
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </ComposerSend>
            </ComposerBar>
          )}
        </Sheet>
      </Overlay>

      {/* ── Bottom Sheets ── */}

      {/* Title sheet */}
      <SheetOverlay $open={sheetKind === 'titulo'} onClick={closeSheet} />
      <BottomSheet $open={sheetKind === 'titulo'}>
        <SheetHandle />
        <SheetTitle>Editar título</SheetTitle>
        <SheetInput
          defaultValue={currentDemand.titulo}
          autoFocus
          id="titulo-input"
          onKeyDown={e => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <SheetActions>
          <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
          <SheetBtn $primary disabled={savingField === 'titulo'} onClick={async () => {
            const val = (document.getElementById('titulo-input') as HTMLInputElement)?.value;
            if (val?.trim()) { await saveField('titulo', { titulo: val }); closeSheet(); }
          }}>
            {savingField === 'titulo' ? <Spinner /> : 'Salvar'}
          </SheetBtn>
        </SheetActions>
      </BottomSheet>

      {/* Date sheet */}
      <SheetOverlay $open={sheetKind === 'previsao'} onClick={closeSheet} />
      <BottomSheet $open={sheetKind === 'previsao'}>
        <SheetHandle />
        <SheetTitle>Previsão de conclusão</SheetTitle>
        <SheetDateInput
          type="date"
          value={sheetDate}
          onChange={e => setSheetDate(e.target.value)}
        />
        <SheetActions>
          <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
          <SheetBtn $primary disabled={savingField === 'previsao_conclusao'} onClick={handleSheetSave}>
            {savingField === 'previsao_conclusao' ? <Spinner /> : 'Salvar'}
          </SheetBtn>
        </SheetActions>
      </BottomSheet>

      {/* Intermediador sheet */}
      <SheetOverlay $open={sheetKind === 'intermediador'} onClick={closeSheet} />
      <BottomSheet $open={sheetKind === 'intermediador'}>
        <SheetHandle />
        <SheetTitle>Intermediador</SheetTitle>
        <SheetInput
          defaultValue={currentDemand.intermediador || ''}
          autoFocus
          id="intermediador-input"
        />
        <SheetActions>
          <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
          <SheetBtn $primary disabled={savingField === 'intermediador'} onClick={async () => {
            const val = (document.getElementById('intermediador-input') as HTMLInputElement)?.value;
            await saveField('intermediador', { intermediador: val || null });
            closeSheet();
          }}>
            {savingField === 'intermediador' ? <Spinner /> : 'Salvar'}
          </SheetBtn>
        </SheetActions>
      </BottomSheet>

      {/* List-option sheets (tipo, natureza, tamanho, cidade, solicitante, responsavel) */}
      {(['tipo','natureza','tamanho','cidade','solicitante','responsavel'] as SheetKind[]).map(kind => {
        const sheetTitles: Record<string, string> = { tipo: 'Tipo', natureza: 'Natureza', tamanho: 'Tamanho', cidade: 'Cidade', solicitante: 'Solicitante', responsavel: 'Responsável' };
        return (
        <React.Fragment key={kind}>
          <SheetOverlay $open={sheetKind === kind} onClick={closeSheet} />
          <BottomSheet $open={sheetKind === kind}>
            <SheetHandle />
            <SheetTitle>
              {sheetTitles[kind]}
            </SheetTitle>
            {sheetNeedsSearch && (
              <SheetInput
                placeholder="Buscar..."
                value={sheetSearch}
                autoFocus
                onChange={e => setSheetSearch(e.target.value)}
              />
            )}
            <SheetList>
              {kind === 'responsavel' && (canAssignResponsible || canUnassignResponsible) && currentDemand.responsavel && canUnassignResponsible && (
                <SheetOption onClick={async () => {
                  setSavingField('responsavel');
                  try {
                    const r = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                      body: JSON.stringify({ responsavel_id: null }),
                    });
                    if (r.ok) { setCurrentDemand({ ...currentDemand, responsavel: undefined }); onUpdate(); }
                  } catch {} finally { setSavingField(null); }
                  closeSheet();
                }}>
                  <span style={{ color: '#ef4444' }}>Remover atribuição</span>
                </SheetOption>
              )}
              {currentSheetOptions.map(opt => {
                let currentVal: string | undefined;
                if (kind === 'tipo') currentVal = currentDemand.tipo;
                else if (kind === 'natureza') currentVal = currentDemand.natureza;
                else if (kind === 'tamanho') currentVal = currentDemand.tamanho;
                else if (kind === 'cidade') currentVal = currentDemand.cidade_id != null ? String(currentDemand.cidade_id) : undefined;
                else if (kind === 'solicitante') currentVal = currentDemand.solicitante_id != null ? String(currentDemand.solicitante_id) : undefined;
                else if (kind === 'responsavel') currentVal = currentDemand.responsavel ? String(currentDemand.responsavel.id) : undefined;
                return (
                  <SheetOption key={opt.value} $active={currentVal === opt.value} onClick={() => handleOptionSelect(opt.value)}>
                    {kind === 'tamanho' && opt.value in SIZE_FILLED ? (
                      <><SizeDots size={opt.value as DemandSize} /> {opt.label}</>
                    ) : opt.label}
                    {currentVal === opt.value && (
                      <svg fill="none" stroke="#3b82f6" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 16, height: 16, marginLeft: 'auto' }}>
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </SheetOption>
                );
              })}
            </SheetList>
            <SheetActions>
              <SheetBtn onClick={closeSheet}>Fechar</SheetBtn>
            </SheetActions>
          </BottomSheet>
        </React.Fragment>
        );
      })}
    </>
  );
};

export default DemandDetailModalMobile;
